import { BrowserRouter as Router, Redirect, Route, RouteProps, Switch } from 'react-router-dom';
import HelmetWrapper from './components/Common/HelmetWrapper';
import { AuthConsumer, AuthProvider } from './components/Authentication/AuthProvider';
import PrivateRoute from './components/Navigation/PrivateRoute';
import LoginCallback from './components/Authentication/LoginCallback';
import LogoutCallback from './components/Authentication/LogoutCallback';
import SilentRenew from './components/Authentication/SilentRenew';
import Logout from './components/Authentication/Logout';
import CaseOverview from './pages/CaseOverview';
import NotFoundPage from './pages/NotFoundPage';
import ForbiddenPage from './pages/ForbiddenPage';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from './tracking/appInsights';
import { AppLanguage } from './types';
import useAppStore from './store/appStore';
import { AppWrapper } from './components/Common/AppWrapper';
import CaseDetails from './pages/CaseDetails';

export const ROUTE = {
  HOME: { DANISH: '/', ENGLISH: '/en' },
  NO_ACCESS: { DANISH: '/ingen-adgang', ENGLISH: '/en/no-access' },
  CASE_DETAILS: { DANISH: '/sagsdetaljer', ENGLISH: '/en/casedetails' },
};

export default function App() {
  const setAppLanguage = useAppStore((state) => state.setAppLanguage);

  const LocalizePrivateRoute = ({ path, ...rest }: RouteProps) => {
    const pathLanguage: AppLanguage = path?.includes('/en')
      ? AppLanguage.English
      : AppLanguage.Danish;
    setAppLanguage(pathLanguage);

    return <PrivateRoute {...rest} path={path} language={pathLanguage} />;
  };
  const LocalizeRoute = ({ path, ...rest }: RouteProps) => {
    const pathLanguage: AppLanguage = path?.includes('/en')
      ? AppLanguage.English
      : AppLanguage.Danish;
    setAppLanguage(pathLanguage);

    return <Route {...rest} path={path} />;
  };

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <Router>
        <AuthProvider
          login={{
            client_id: runtimeConfig.clientId || '',
            authority: runtimeConfig.authority || '',
          }}
        >
          <AuthConsumer>
            {({ getUsername, isAuthenticated }) => (
              <AppWrapper
                username={getUsername() ?? ''}
                isAuthenticated={isAuthenticated() ?? false}
              >
                <>
                  <HelmetWrapper />
                  <Switch>
                    {/* Login related pages */}
                    <Route exact path="/signin-oidc" component={LoginCallback} />
                    <Route path="/signout-callback-oidc" component={LogoutCallback} />
                    <Route path="/silent-renew-oidc" component={SilentRenew} />
                    <PrivateRoute path="/logout" component={Logout} roles={[]} />
                    <Redirect from="/da" to={ROUTE.HOME.DANISH} />

                    <LocalizePrivateRoute exact path={ROUTE.HOME.DANISH} component={CaseOverview} />
                    <LocalizePrivateRoute
                      exact
                      path={ROUTE.HOME.ENGLISH}
                      component={CaseOverview}
                    />

                    <LocalizeRoute exact path={ROUTE.NO_ACCESS.DANISH} component={ForbiddenPage} />
                    <LocalizeRoute exact path={ROUTE.NO_ACCESS.ENGLISH} component={ForbiddenPage} />
                    <LocalizePrivateRoute
                      exact
                      path={`${ROUTE.CASE_DETAILS.DANISH}/:caseId`}
                      component={CaseDetails}
                    />
                    <LocalizePrivateRoute
                      exact
                      path={`${ROUTE.CASE_DETAILS.ENGLISH}/:caseId`}
                      component={CaseDetails}
                    />

                    <PrivateRoute component={NotFoundPage} />
                  </Switch>
                </>
              </AppWrapper>
            )}
          </AuthConsumer>
        </AuthProvider>
      </Router>
    </AppInsightsContext.Provider>
  );
}
